// ============================================================
// THE COMPANY — DecisionLog Component (Board View tab)
// ============================================================

import { useMemo } from 'react';
import { useCompanyStore } from '../../store/useCompanyStore';

export default function DecisionLog() {
  const decisions = useCompanyStore((s) => s.decisions);
  const roles = useCompanyStore((s) => s.roles);

  // Sort decisions newest-first
  const sortedDecisions = useMemo(
    () =>
      [...decisions].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [decisions],
  );

  if (sortedDecisions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <svg
          className="h-10 w-10 mb-3"
          style={{ color: 'var(--text-muted)' }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
          />
        </svg>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          No decisions tracked yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sortedDecisions.map((decision) => {
        const maker = roles.find((r) => r.id === decision.roleId);
        const dateStr = new Date(decision.createdAt).toLocaleDateString(
          undefined,
          { year: 'numeric', month: 'short', day: 'numeric' },
        );

        // Truncate long decision content
        const displayContent =
          decision.content.length > 200
            ? decision.content.slice(0, 200) + '...'
            : decision.content;

        return (
          <div
            key={decision.id}
            className="rounded-lg p-4"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            {/* Decision content */}
            <p
              className="text-sm leading-relaxed mb-3"
              style={{ color: 'var(--text-primary)' }}
            >
              {displayContent}
            </p>

            {/* Meta row: author + date */}
            <div
              className="flex items-center gap-2 mb-3 text-xs"
              style={{ color: 'var(--text-muted)' }}
            >
              <span className="font-medium" style={{ color: 'var(--text-secondary)' }}>
                {maker ? `${maker.name}, ${maker.title}` : 'Unknown'}
              </span>
              <span>&middot;</span>
              <span>{dateStr}</span>
            </div>

            {/* Awareness indicators */}
            <div className="flex items-center gap-1 flex-wrap">
              <span
                className="text-[11px] font-medium mr-1.5 shrink-0"
                style={{ color: 'var(--text-muted)' }}
              >
                Awareness:
              </span>
              <div className="flex items-center gap-1.5 overflow-x-auto">
                {roles
                  .filter((r) => r.id !== decision.roleId)
                  .map((role) => {
                    const isInformed = decision.informedRoleIds.includes(
                      role.id,
                    );
                    return (
                      <span
                        key={role.id}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium shrink-0"
                        style={{
                          backgroundColor: isInformed
                            ? 'rgba(34, 197, 94, 0.1)'
                            : 'rgba(239, 68, 68, 0.1)',
                          color: isInformed ? '#22c55e' : '#ef4444',
                          border: `1px solid ${isInformed ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                        }}
                        title={
                          isInformed
                            ? `${role.name} is informed`
                            : `${role.name} is NOT informed`
                        }
                      >
                        <span
                          className="h-1.5 w-1.5 rounded-full shrink-0"
                          style={{
                            backgroundColor: isInformed ? '#22c55e' : '#ef4444',
                          }}
                        />
                        {role.name}
                      </span>
                    );
                  })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
